import React, { useState } from 'react';
import { useAppState } from '../hooks/useAppState';
import { apiService } from '../services/api';
import { isValidEmail, isValidPhone, isValidName, isValidZipCode, getCleanPhoneNumber, isValidCarPlate } from '../utils/validation';
import { getConfig, resolveExistingCards } from '../services/configManager';
import BackButton from '../components/BackButton';
import PhoneInput from '../components/PhoneInput';
import { useCardPaymentModal } from '../hooks/useCardPaymentModal';
import { formatDate } from '../utils/validation';
import { TicketStatus } from '../types/api';

const MemberRegistrationPage: React.FC = () => {
  const { state, setLoading, setError, setStep, setOrderResponse } = useAppState();
  const { currentTicket } = state;
  const fee = getConfig().FEATURES.Fee.Amount;
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    zipCode: '',
    generalTerms: false,
    vehicleNumber: currentTicket?.vehicleNumber
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validatedRequest, setValidatedRequest] = useState<any>(null);
  const cardPaymentModal = useCardPaymentModal();


  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!isValidName(formData.firstName)) {
      newErrors.firstName = 'First name is required (max 50 characters)';
    }

    if (!isValidName(formData.lastName)) {
      newErrors.lastName = 'Last name is required (max 50 characters)';
    }

    if (!isValidEmail(formData.email)) {
      newErrors.email = 'Valid email is required (max 50 characters)';
    }

    if (!isValidPhone(formData.phone)) {
      newErrors.phone = 'Valid phone number is required';
    }

    if (!isValidZipCode(formData.zipCode)) {
      newErrors.zipCode = 'Zip code is required';
    }

    // if (!isValidCarPlate(formData?.vehicleNumber || '')) {
    //   newErrors.vehicleNumber = 'Valid vehicle number is required';
    // }

    if (!formData.generalTerms) {
      newErrors.generalTerms = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !currentTicket) return;

    setLoading(true);
    try {
      const contact = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email || null,
        phone: getCleanPhoneNumber(formData.phone) || null
      };

      const billingAddress = {
        street: null,
        city: null,
        state: null,
        postCode: formData.zipCode
      };

      const request = await apiService.createPurchaseRequest(
        contact,
        billingAddress,
        true, // isMember
        currentTicket.ticketId || undefined,
        currentTicket.ticketNumber || undefined,
        formData.generalTerms,
        formData.vehicleNumber
      );

      await apiService.validateOrder(request);
      setValidatedRequest(request);
      setLoading(false);

      const result = await cardPaymentModal.show('Credit Card Details', resolveExistingCards());

      if (result.status !== 'success') return;

      setLoading(true);

      let updatedRequest;
      if (result.selected === 'newCard' && result.newCard) {
        updatedRequest = apiService.updateRequestWithSid(request, result.newCard.sid);
      } else {
        updatedRequest = apiService.updateRequestWithSid(request, '');
      }

      const orderResponse = await apiService.purchaseOrder(updatedRequest);
      setOrderResponse(orderResponse);
      setLoading(false);
      setStep('ticket-void-waiting');
    } catch (error) {
      if (error instanceof Error) {
        const validationErrors = (error as any).errors;
        setError(error.message, validationErrors);
      } else {
        setError('Registration failed');
      }
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="flex-1 flex flex-col p-5">
      <div className="mb-4">
        <BackButton targetStep="ticket-summary" />
      </div>

      <div className="flex-1 flex items-center justify-center">
        <div className="container-card w-full max-w-sm">

          <h1 className="text-title font-extrabold text-black text-center mb-8">
            Join as a Member
          </h1>
          <div className="flex flex-col gap-4 mb-8">
            <div className="flex justify-between">
              <span className="text-black text-md">Ticket:</span>
              <span className="text-black text-md font-semibold">
                {currentTicket?.ticketNumber || 'N/A'}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-black text-md">Parking Lot:</span>
              <span className="text-black text-md font-semibold">
                {currentTicket?.spot?.name || 'N/A'}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-black text-md">Car Plate:</span>
              <span className="text-black text-md font-semibold">
                {currentTicket?.vehicleNumber || 'N/A'}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-black text-md">Entry:</span>
              <span className="text-black text-md font-semibold">
                {formatDate(currentTicket?.entryDate || '')}
              </span>
            </div>

            {currentTicket?.exitDate && (
              <div className="flex justify-between">
                <span className="text-black text-md">Exit:</span>
                <span className="text-black text-md font-semibold">
                  {formatDate(currentTicket?.exitDate || '')}
                </span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-black text-md">Status:</span>
              <span className={`text-md font-semibold ${currentTicket?.status === TicketStatus.active ? 'text-green-600' :
                currentTicket?.status === TicketStatus.paid ? 'text-blue-600' :
                  currentTicket?.status === TicketStatus.voided ? 'text-orange-600' :
                    'text-red-600'
                }`}>
                {currentTicket?.status.toUpperCase()}
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-black text-md">First Name <span className="required-asterisk">*</span></label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className={`input-field ${errors.firstName ? 'border-red-500' : ''}`}
                maxLength={50}
              />
              {errors.firstName && <span className="text-red-500 text-sm">{errors.firstName}</span>}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-black text-md">Last Name <span className="required-asterisk">*</span></label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className={`input-field ${errors.lastName ? 'border-red-500' : ''}`}
                maxLength={50}
              />
              {errors.lastName && <span className="text-red-500 text-sm">{errors.lastName}</span>}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-black text-md">Email <span className="required-asterisk">*</span></label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                maxLength={50}
              />
              {errors.email && <span className="text-red-500 text-sm">{errors.email}</span>}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-black text-md">Phone <span className="required-asterisk">*</span></label>
              <PhoneInput
                value={formData.phone}
                onChange={(phone) => handleInputChange('phone', phone)}
                hasError={!!errors.phone}
              />
              {errors.phone && <span className="text-red-500 text-sm">{errors.phone}</span>}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-black text-md">Zip Code <span className="required-asterisk">*</span></label>
              <input
                type="text"
                value={formData.zipCode}
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
                className={`input-field ${errors.zipCode ? 'border-red-500' : ''}`}
              />
              {errors.zipCode && <span className="text-red-500 text-sm">{errors.zipCode}</span>}
            </div>

            {/* <div className="flex flex-col gap-2">
              <label className="text-black text-md">Car Plate <span className="required-asterisk">*</span></label>
              <input
                type="text"
                value={formData.vehicleNumber || ''}
                onChange={(e) => handleInputChange('vehicleNumber', e.target.value)}
                className={`input-field ${errors.vehicleNumber ? 'border-red-500' : ''}`}
                maxLength={10}
              />
              {errors.vehicleNumber && <span className="text-red-500 text-sm">{errors.vehicleNumber}</span>}
            </div> */}

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.generalTerms}
                onChange={(e) => handleInputChange('generalTerms', e.target.checked)}
                className="h-4 w-4"
              />
              <label className="text-black text-md">
                I accept the{' '}
                <a
                  href={`${getConfig().ASSETS.BASE_URL}${getConfig().ASSETS.FILES.TERMS_AND_CONDITIONS}`}
                  className="text-blue-600 underline hover:text-blue-800"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms & Conditions
                </a>
                {' '}and the{' '}
                <a
                  href={`${getConfig().ASSETS.BASE_URL}${getConfig().ASSETS.FILES.PRIVACY_POLICY}`}
                  className="text-blue-600 underline hover:text-blue-800"
                  target="_blank"
                  rel="noopener noreferrer"
                >Privacy Policy</a>
                {' '}<span className="required-asterisk">*</span>
              </label>
            </div>
            {errors.generalTerms && <span className="text-red-500 text-sm">{errors.generalTerms}</span>}

            <button
              onClick={handleSubmit}
              className="btn-primary w-full mt-4"
            >
              Credit Card Details
            </button>

            {fee && <p className="text-sm text-gray-600 text-center mt-2">
              An additional fee of {fee} will be added to your stay
            </p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberRegistrationPage; 